import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { AccountRolesFetchedAction } from '@Application/Security/Roles/Actions/accountRolesFetched';
import type { RoleCreatedAction } from '@Application/Security/Roles/Actions/roleCreated';
import type { RoleDeletedAction } from '@Application/Security/Roles/Actions/roleDeleted';
import type { RoleUpdatedAction } from '@Application/Security/Roles/Actions/roleUpdated';
import type { RolesFetchedAction } from '@Application/Security/Roles/Actions/rolesFetched';
import AccountRolesListView from '@UI/Store/Roles/Views/AccountRolesListView';
import RoleView from '@UI/Store/Roles/Views/RoleView';
import RolesListView from '@UI/Store/Roles/Views/RolesListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type RolesStore = {
	rolesList: RolesListView | null;
	accountRoles: AccountRolesListView | null;
};

const initialState: RolesStore = {
	rolesList: null,
	accountRoles: null,
};

// Two distinct locations, never one: `rolesList` is the Roles screen's own filtered, paginated list;
// `accountRoles` is the user-management role selector's unfiltered catalogue. Each action writes only
// its own location, so a `ROLES_FETCHED` can never overwrite `accountRoles` and an
// `ACCOUNT_ROLES_FETCHED` can never overwrite `rolesList`.
type Actions =
	| RolesFetchedAction
	| AccountRolesFetchedAction
	| RoleCreatedAction
	| RoleUpdatedAction
	| RoleDeletedAction
	| UserRevokedAction;

const whenRolesFetched = (store: RolesStore, action: RolesFetchedAction): RolesStore => ({
	...store,
	rolesList: new RolesListView(
		action.payload.results.map((role) => new RoleView(role)),
		action.accountId,
		action.payload.totalRecords,
	),
});

const whenAccountRolesFetched = (store: RolesStore, action: AccountRolesFetchedAction): RolesStore => ({
	...store,
	accountRoles: new AccountRolesListView(
		action.payload.results.map((role) => new RoleView(role)),
		action.accountId,
		action.payload.totalRecords,
	),
});

// The Roles screen refetches `rolesList` itself; the catalogue of the same account is emptied for the
// user-management selector's next mount.
const whenRoleChanged = (
	store: RolesStore,
	action: RoleCreatedAction | RoleUpdatedAction | RoleDeletedAction,
): RolesStore => {
	if (store.accountRoles === null || store.accountRoles.accountId !== action.accountId) {
		return store;
	}

	return { ...store, accountRoles: null };
};

const whenUserRevoked = (): RolesStore => initialState;

export default registerStore<'rolesStore', Actions>(
	'rolesStore',
	createSliceReducer({
		ROLES_FETCHED: whenRolesFetched,
		ACCOUNT_ROLES_FETCHED: whenAccountRolesFetched,
		ROLE_CREATED: whenRoleChanged,
		ROLE_UPDATED: whenRoleChanged,
		ROLE_DELETED: whenRoleChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
